import React, { useEffect, useMemo, useState } from 'react';
import { auth as authService } from '../apiService';

const EmailIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
    </svg>
);

const LockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
);

export default function AuthSection({ onLogin, initialMode = 'FLARE' }) {
    const activeColor = 'rgb(196, 21, 28)';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginMode, setLoginMode] = useState(initialMode);
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [slideIndex, setSlideIndex] = useState(initialMode === 'MIXFM' ? 1 : 0);

    useEffect(() => {
        setLoginMode(initialMode);
        setSlideIndex(initialMode === 'MIXFM' ? 1 : 0);
    }, [initialMode]);

    const slides = useMemo(() => ([
        {
            key: 'FLARE',
            title: 'Welcome to',
            logo: '/Flare Logo.png',
            fallbackLogo: '/Flare-Pakistan-Logo.png',
            subtitle: ''
        },
        {
            key: 'MIXFM',
            title: 'Welcome to',
            logo: '/Header.png',
            fallbackLogo: '/Mix FM Logo 99.png',
            subtitle: 'Mix FM Billing'
        }
    ]), []);

    useEffect(() => {
        const id = window.setInterval(() => {
            setSlideIndex((prev) => (prev + 1) % slides.length);
        }, 3500);
        return () => window.clearInterval(id);
    }, [slides.length]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);
        try {
            const data = await authService.login(email, password);
            onLogin(data.user, loginMode === 'FLARE' ? 'FLARE' : 'MIXFM');
            if (rememberMe) localStorage.setItem('rememberMe', 'true');
            else localStorage.removeItem('rememberMe');
        } catch {
            setError("Invalid email or password.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col lg:flex-row bg-white">
            <div className={`relative lg:w-1/2 flex flex-col items-center justify-center p-8 py-12 text-center overflow-hidden ${slides[slideIndex].key === 'MIXFM' ? 'bg-gradient-to-br from-red-700 to-red-900 text-white' : 'bg-slate-50 text-gray-900'}`}>
                {slides[slideIndex].key === 'MIXFM' ? (
                    <>
                        <div className="relative z-10 flex-grow flex flex-col items-center justify-center">
                            <h1 className="text-5xl md:text-6xl font-extrabold mb-4">Welcome to Mix FM</h1>
                            <h2 className="text-2xl md:text-3xl font-light mb-12">Radio Station Network</h2>
                            <div className="flex justify-center items-center gap-x-2 md:gap-x-8">
                                <div className="bg-white rounded-lg p-1 md:p-2 shadow-lg">
                                    <img src="/Mix FM Logo 99.png" alt="Mix FM 99 Logo" className="h-12 md:h-20 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                </div>
                                <div className="bg-white rounded-lg p-1 md:p-2 shadow-lg">
                                    <img src="/Mix FM Logo 100.png" alt="Mix FM 100 Logo" className="h-12 md:h-20 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                </div>
                                <div className="bg-white rounded-lg p-1 md:p-2 shadow-lg">
                                    <img src="/Mix FM Logo 96.png" alt="Mix FM 96 Logo" className="h-12 md:h-20 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="absolute -top-16 -left-16 w-72 h-72 bg-red-100 rounded-full opacity-50"></div>
                        <div className="absolute -bottom-24 -right-10 w-96 h-96 border-4 border-red-100 rounded-full opacity-50"></div>
                        <div className="relative z-10 flex-grow flex flex-col items-center justify-center">
                            <h2 className="text-2xl md:text-3xl font-light text-gray-600 mb-4">{slides[slideIndex].title}</h2>
                            <img
                                src={slides[slideIndex].logo}
                                alt="Flare Logo"
                                className="h-32 md:h-44 w-auto mx-auto"
                                onError={(e) => { e.currentTarget.src = slides[slideIndex].fallbackLogo; }}
                            />
                            {slides[slideIndex].subtitle ? <p className="mt-4 text-sm font-semibold text-gray-500">{slides[slideIndex].subtitle}</p> : null}
                        </div>
                        <div className="relative z-10 w-full mt-12 pt-8 border-t border-slate-200">
                            <p className="text-sm font-semibold text-gray-500 mb-4">Also a part of the Mix FM Network</p>
                            <div className="flex justify-center items-center gap-x-4 sm:gap-x-6">
                                <img src="/Mix FM Logo 99.png" alt="Mix FM 99" className="h-16 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                <img src="/Mix FM Logo 100.png" alt="Mix FM 100" className="h-16 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                <img src="/Mix FM Logo 96.png" alt="Mix FM 96" className="h-16 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            </div>
                        </div>
                    </>
                )}

                <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 z-20">
                    {slides.map((s, idx) => (
                        <button
                            key={s.key}
                            type="button"
                            onClick={() => setSlideIndex(idx)}
                            className={`h-2.5 w-2.5 rounded-full ${idx === slideIndex ? (slides[slideIndex].key === 'MIXFM' ? 'bg-white' : 'bg-red-600') : (slides[slideIndex].key === 'MIXFM' ? 'bg-white/40' : 'bg-red-200')}`}
                        />
                    ))}
                </div>
            </div>

            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <div className="w-full max-w-sm">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Log in to your <span style={{ color: activeColor }}>{loginMode === 'FLARE' ? 'Flare Account' : 'Radio Account'}</span></h2>
                    <p className="text-gray-500 mb-8">Welcome back! Please enter your details.</p>
                    <div className="flex bg-slate-100 p-1 rounded-lg mb-6">
                        <button
                            type="button"
                            onClick={() => setLoginMode('MIXFM')}
                            className={`flex-1 py-2 text-xs font-bold rounded-md transition-all ${loginMode === 'MIXFM' ? 'text-white shadow-lg' : 'text-slate-600 hover:text-slate-900'}`}
                            style={loginMode === 'MIXFM' ? { backgroundColor: activeColor } : undefined}
                        >
                            Radio Login
                        </button>
                        <button
                            type="button"
                            onClick={() => setLoginMode('FLARE')}
                            className={`flex-1 py-2 text-xs font-bold rounded-md transition-all ${loginMode === 'FLARE' ? 'text-white shadow-lg' : 'text-slate-600 hover:text-slate-900'}`}
                            style={loginMode === 'FLARE' ? { backgroundColor: activeColor } : undefined}
                        >
                            Flare Login
                        </button>
                    </div>
                    <form onSubmit={handleLogin} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                            <div className="relative mt-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <EmailIcon />
                                </div>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                                    placeholder="you@example.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                            <div className="relative mt-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <LockIcon />
                                </div>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="block w-full pl-10 pr-16 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                                    placeholder="Enter your password"
                                />
                                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-4 flex items-center text-sm font-semibold text-gray-600">
                                    {showPassword ? 'Hide' : 'Show'}
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <label className="flex items-center">
                                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500" />
                                <span className="ml-2 text-sm text-gray-700">Remember me</span>
                            </label>
                        </div>
                        {error && <div className="p-3 rounded-md bg-red-100 text-red-700 text-center font-semibold">{error}</div>}
                        <button type="submit" disabled={isSubmitting} className="w-full py-3 px-6 rounded-xl shadow-lg font-bold text-white transition-all disabled:bg-gray-400" style={{ backgroundColor: activeColor }}>
                            {isSubmitting ? 'Logging in...' : 'Login'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
